// ── L'ESPACE SÉJOUR v2 — les écrans ─────────────────────────────────────────
//
// Le montage du dessin de Dimitri (`docs/handoff/voyageur-proto.html`), pour
// les deux parcours et les trois moments. Ce fichier ne contient QUE de la
// présentation : toutes les décisions vivent dans `src/lib/sejour-model.js`
// (le modèle) et `src/lib/sejour-exceptions.js` (les onze chemins). Un écran
// qui déciderait quelque chose serait un écran qu'on ne peut pas tester.
//
// ═══ CE QUE CE MONTAGE CORRIGE, ET QUI ÉTAIT MESURÉ ═══
// La rangée des étapes était bâtie en trois colonnes dont la largeur venait du
// mot le plus long : 412 px réclamés pour 314 disponibles, une étape rognée et
// injoignable, et au départ la page entière qui part en travers. Ici le fil est
// une COLONNE : une pastille de 26 px, un filet, et le texte qui prend ce qui
// reste. Il ne peut plus déborder — pas parce qu'on a réglé une largeur, parce
// qu'il n'y a plus qu'une colonne à faire tenir.
//
// ═══ LES RÈGLES TENUES ICI, ET COMMENT LES CASSER ═══
//   · 44 px de haut minimum sur TOUT ce qui se touche. Un lien de 17 px de haut
//     dans un paragraphe n'est pas une cible : c'est un piège.
//   · Jamais de bouton mort. Chaque geste est rendu SOUS CONDITION de son
//     adresse (`filter(Boolean)` sur les listes, `&&` sur les boutons). Si la
//     donnée manque, le bloc disparaît — il ne montre ni « — » ni « 0,00 € ».
//   · Un seul bouton plein par écran [D5] : celui du geste du moment.
//   · Le contact n'apparaît qu'une fois [D7] : quand la page porte son bloc
//     « nous joindre », la page hôte est montée sans son pied de contact.
//
// ═══ LE CODE D'ENTRÉE EN CLAIR — DÉCISION ROUVERTE (Dimitri, 2026-09-05) ═══
// L'ancienne doctrine (« un lien livret égaré ne doit pas devenir un code de
// porte en clair ») interdisait le code ici. Dimitri l'a ROUVERTE, au mot :
// « Le code doit apparaître sur l'espace voyageur au moment exact où les
// instructions s'envoient — même déclencheur, même condition. […] Le lien
// reste protégé par ses conditions — sans formulaire et sans caution, pas de
// code. C'est ça qui protège, pas le fait de le cacher à quelqu'un qui a tout
// réglé. »
// LE VERROU EST SERVEUR, JAMAIS ICI : `api/sites.js` ne sert `code_acces` que
// si (a) les conditions de l'envoi sont remplies ET (b) le moment des
// instructions est atteint (réglage unique lib/instructions-schedule.js —
// midi le jour J par défaut, le même instant que le message et le livret).
// Cet écran ne DÉCIDE rien : clé absente → BlocAttenteCode (« vos
// instructions arrivent le [date] à [heure] ») ; clé servie → BlocCodeAcces,
// qui montre CE QUI EXISTE (une donnée absente est absente, jamais « 0000 »).

import React from "react";
import { C, F } from "./lib/charte-sejour.js";
// LOGO_B64 = le logo COMPLET (lotus + son disque) · LOGO_WHITE_B64 = le LOTUS
// SEUL, sans disque — utilisable uniquement comme POCHOIR (cf. `Lotus`), jamais
// affiché tel quel sur un fond foncé (il ne resterait que des traits flottants).
import { LOGO_B64, LOGO_WHITE_B64 } from "./assets/logo";
import {
  chemin as lireChemin,
  enrichirAnnule,
  enrichirDelaiEcoule,
  enrichirSignatureExpiree,
  resteSurLeBlocage,
  resteSurLaCaducite,
  TEXTE_BLOCAGE,
  TEXTE_BLOCAGE_SANS_CHRONO,
  TEXTE_CADUCITE,
} from "./lib/sejour-exceptions.js";
// [v4.1 D36, 2026-09-05] LE BLOC D'ARRIVÉE REND LA FICHE DU LIVRET — avec LES
// helpers du livret, jamais des copies : l'embed vidéo (videoUrlToEmbed), la
// langue (pickLang), le texte riche (sanitizeLivretHtml / parseLivretMarkdown).
import { videoUrlToEmbed } from "./lib/livret-video-client";
import { pickLang } from "./lib/pickLang";
import { sanitizeLivretHtml } from "./lib/livret-sanitize-client";
import { parseLivretMarkdown } from "./lib/livret-markdown";
import { libellesEtapeCaution } from "./lib/caution-phrase.js";

// ── Les libellés des étapes ────────────────────────────────────────────────
// Repris de l'existant. Les variantes `fait` sont les petites pastilles vertes.
/** Les textes d'une étape ; pour la caution, ceux du mode servi par le serveur. */
function textesEtape(cle, modele) {
  const T = ETAPES[cle] || {};
  return cle === "caution" && modele
    ? { ...T, ...libellesEtapeCaution(modele.modeCaution, modele.seuilCaution) }
    : T;
}

const ETAPES = {
  signature: {
    titre: "Signature du contrat",
    fait: "signé",
    done: "Contrat signé.",
    active:
      "Lisez puis signez votre contrat de location ci-dessous pour pouvoir régler votre séjour.",
    geste: "Je signe",
    sous: "Votre signature vaut acceptation",
  },
  paiement: {
    titre: "Payer le séjour",
    fait: "payé",
    done: "Paiement du séjour reçu.",
    // « Le mot suit l'état, pas l'écran » (Dimitri, arrêt 3, 2026-09-05) :
    // avant paiement c'est une DEMANDE — le paiement la TRANSFORME en
    // réservation, et la phrase le dit dans ce sens-là.
    active: "Le paiement transforme votre demande en réservation. Rapide et sécurisé.",
    attente: "Signez d’abord votre contrat.",
    geste: "Payer mon séjour",
    sous: "Paiement sécurisé · vous recevrez un reçu par email",
    // [arrêt 3, 2026-09-05] L'encadré de la carte « payer » — LE MÊME texte
    // que l'ancien écran (`reassure` de JourneyPage) et que la maquette
    // (`TXT.stay.rea`), au mot près. Rendu par CarteDuMoment entre le prix et
    // le bouton, comme au prototype.
    // L'encadré des 30 minutes a QUITTÉ cette carte (Dimitri, arrêt 3,
    // 2026-09-05) : « les 30 minutes n'apparaissent qu'après le clic, sur la
    // page de paiement. » Deux délais, deux affichages — ici seul le 48 h
    // parle, en heures et en phrase.
    rea: null,
  },
  formulaire: {
    titre: "Formulaire de bienvenue",
    fait: "reçu",
    done: "Formulaire de bienvenue reçu. Merci !",
    active: "Heure d’arrivée, coordonnées, pièce d’identité.",
    attente: "Disponible une fois votre séjour réglé.",
    geste: "Remplir mon formulaire de bienvenue",
  },
  caution: {
    titre: "Caution",
    fait: "déposée",
    // Lot A5 (30/10) : plus de promesse « sans débit » en dur — au-delà du seuil
    // la caution est prélevée. Ces textes-ci sont les neutres de la source
    // unique ; l'écran prend ceux du mode servi par textesEtape(cle, modele).
    ...libellesEtapeCaution(null),
    // [D16] — le motif d'attente est DIT, il n'est pas laissé à deviner.
    attente_formulaire: "Remplissez d’abord votre formulaire de bienvenue.",
    geste: "Déposer la caution",
  },
  confirmation: {
    titre: "Confirmation & arrivée",
    titreArrive: "Arrivée",
    fait: "confirmé",
    done: "Réservation confirmée. Vos instructions d’arrivée sont disponibles dans le livret.",
    attente:
      "Vos instructions d’arrivée arrivent une fois votre préparation terminée.",
    doneArrive: "Vous y êtes. Bon séjour !",
    // « Vous y êtes. » ET RIEN DE PLUS quand tout n'est pas réglé (Dimitri,
    // arrêt 8, 2026-09-05) : la version longue promettait un livret
    // « accessible à tout moment » alors qu'il était VERROUILLÉ trois cartes
    // plus bas. D4 — un seul message de verrou par écran, et c'est la carte
    // du livret qui le porte. La version longue ne vit que tout-réglé.
    attenteArrive: "Vous y êtes.",
    attenteArriveToutRegle: "Vous y êtes. Votre livret reste accessible à tout moment.",
  },
};

// ── Briques ────────────────────────────────────────────────────────────────

/** Toute cible tactile passe par ici : 44 px, jamais moins. */
const CIBLE = { minHeight: 44, display: "flex", alignItems: "center" };

export function BoutonPlein({
  children,
  onClick,
  href,
  sous,
  testId,
  disabled,
}) {
  // Jamais de bouton mort : sans geste ET sans adresse, on ne rend rien.
  if (!onClick && !href) return null;
  const style = {
    ...CIBLE,
    justifyContent: "center",
    gap: 9,
    width: "100%",
    minHeight: 52,
    borderRadius: 14,
    // L'ombre de la maquette (.cta) — relevée dans la passe « décoratif »
    // du 2026-09-05, absente du montage jusqu'ici.
    boxShadow: "0 3px 14px rgba(35,50,64,.22)",
    background: C.sejourFond, // MESURÉ : #233240 au prototype, pas #3d5166.
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    fontFamily: F.sans,
    border: "none",
    textDecoration: "none",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.55 : 1,
  };
  const contenu = href ? (
    <a data-testid={testId} href={href} style={style}>
      {children}
    </a>
  ) : (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={style}
    >
      {children}
    </button>
  );
  return (
    <>
      {contenu}
      {sous ? (
        <span
          style={{
            display: "block",
            textAlign: "center",
            fontSize: 11.5,
            color: C.inkSoft,
            marginTop: 8,
            lineHeight: 1.45,
          }}
        >
          {sous}
        </span>
      ) : null}
    </>
  );
}

/**
 * L'EMPREINTE DU LOTUS — le logo peint, jamais le logo affiché.
 *
 * Le PNG sert de POCHOIR (`mask-image`) et la forme est PEINTE dans la couleur
 * demandée. C'est la mécanique DÉJÀ en place sur la carte d'action du chemin de
 * repli, reprise telle quelle et pour la même raison : l'asset masqué est LE
 * LOTUS SEUL, alors que la couche alpha du logo complet est un disque plein (le
 * lotus y est peint en blanc) — un masque en aurait fait une pastille ronde.
 *
 * Repli quand `mask` n'est pas supporté : la forme reste INVISIBLE (fond
 * transparent). Jamais un logo en clair, jamais une image cassée.
 */
export function Lotus({ taille, couleur }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "block",
        width: taille,
        height: taille,
        flexShrink: 0,
        backgroundColor: couleur,
        WebkitMaskImage: `url(data:image/png;base64,${LOGO_WHITE_B64})`,
        maskImage: `url(data:image/png;base64,${LOGO_WHITE_B64})`,
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

/**
 * Le bandeau. Prénom quand on l'a [D14], dates, pastille temporelle, anneau.
 * `sous` est omis quand il n'y a rien à dire — pas de ligne vide.
 *
 * `photo` (ADDITIVE, formulaire v2) : image du logement en fond du bandeau,
 * sous un voile dégradé navy (maquette formulaire-proto `.hero .photo`). Le
 * défaut `null` rend un DOM STRICTEMENT IDENTIQUE à avant — l'espace séjour
 * (en prod) ne passe jamais par ce chemin.
 */
export function Bandeau({ titre, quand, dates, anneau, ligne, testId, marque = null, photo = null }) {
  const R = 17;
  const CIRC = 2 * Math.PI * R;
  const part = anneau && anneau.total ? anneau.faites / anneau.total : 0;
  // Le contenu du bandeau (marque, titre, pastille/dates, anneau). Avec photo,
  // il est enveloppé dans un calque relatif z-index 2 (au-dessus du voile) ;
  // sans photo, il est rendu TEL QUEL — DOM identique à avant (fragment).
  const contenu = (
    <>
      {marque}
      <h1
        style={{
          fontFamily: F.serif,
          fontSize: 20, // MESURÉ au prototype ; le montage était à 22.
          fontWeight: 600,
          lineHeight: 1.25,
          margin: 0,
          letterSpacing: "-0.02em",
        }}
      >
        {titre}
      </h1>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 9,
        }}
      >
        {quand ? (
          <span
            data-testid="sejour-pastille-temps"
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 26,
              padding: "0 11px",
              borderRadius: 13,
              background: C.ciel,
              color: C.bleu,
              fontSize: 11.5,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {quand}
          </span>
        ) : null}
        {dates ? (
          <span style={{ fontSize: 12.5, color: "rgba(255,255,255,.75)" }}>
            {dates}
          </span>
        ) : null}
      </span>
      {ligne ? (
        <div
          data-testid="sejour-anneau"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 14,
            background: "rgba(255,255,255,.12)",
            borderRadius: 13,
            padding: "12px 13px",
          }}
        >
          {anneau && anneau.total ? (
            <span
              style={{
                width: 38,
                height: 38,
                flexShrink: 0,
                position: "relative",
              }}
            >
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                style={{ transform: "rotate(-90deg)" }}
              >
                <circle
                  cx="19"
                  cy="19"
                  r={R}
                  fill="none"
                  stroke="rgba(255,255,255,.22)"
                  strokeWidth="3"
                />
                <circle
                  cx="19"
                  cy="19"
                  r={R}
                  fill="none"
                  stroke={C.ciel}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={CIRC}
                  strokeDashoffset={CIRC * (1 - part)}
                />
              </svg>
              <b
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {/* LE FAIT, PAS L'ÉTAPE COURANTE (maquette : `d.ringDone/d.tot`).
                    L'arc et le chiffre disent la MÊME chose — le montage
                    affichait l'ordinal au centre d'un arc qui comptait les
                    faits : « 3/4 » sur un arc aux deux tiers. */}
                {anneau.faites}/{anneau.total}
              </b>
            </span>
          ) : null}
          <span style={{ flex: 1, minWidth: 0 }}>
            <b
              style={{
                display: "block",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.35,
              }}
            >
              {ligne[0]}
            </b>
            {ligne[1] ? (
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "rgba(255,255,255,.75)",
                  marginTop: 3,
                  lineHeight: 1.45,
                }}
              >
                {ligne[1]}
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
    </>
  );
  return (
    <div
      data-testid={testId || "sejour-bandeau"}
      style={{
        // MESURÉ SUR LE PROTOTYPE (2026-09-05) : fond #233240, rayon 0,
        // pleine largeur. Le montage l'avait éclairci et arrondi.
        background: C.sejourFond,
        color: "#fff",
        padding: "15px 16px 17px",
        borderRadius: 0,
        marginBottom: 14,
        // LE HALO (maquette `.hero::after`, relevé par Dimitri le 2026-09-05 —
        // ma grille mesurait couleurs et tailles, pas le décoratif) : un grand
        // cercle ciel translucide qui déborde du coin haut droit. Il donne la
        // profondeur du fond ; sans lui le navy est plat.
        position: "relative",
        overflow: "hidden",
        ...(photo ? { paddingTop: 0 } : null),
      }}
    >
      {/* PHOTO DE FOND (formulaire v2, maquette `.hero .photo` + son voile
          `::after`) — rendue UNIQUEMENT quand `photo` est fourni. Le voile va
          de rgba(35,50,64,.35) à .94 pour que le texte blanc reste lisible. */}
      {photo ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background: `url(${photo}) center / cover no-repeat`,
          }}
        >
          <span
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom, rgba(35,50,64,.35) 0%, rgba(35,50,64,.72) 55%, rgba(35,50,64,.94) 100%)",
            }}
          />
        </span>
      ) : null}
      {/* LIGNE DE MARQUE (Dimitri 2026-09-04) — le lotus et FULLBNBZEN vivent
          DANS le bandeau, sur sa première ligne. Avant, un en-tête blanc les
          portait AU-DESSUS : deux lignes d'en-tête sur le premier écran d'un
          téléphone, pour une seule information. La hauteur gagnée profite au
          contenu, qui est ce que le voyageur est venu chercher. */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: -50,
          right: -40,
          width: 160,
          height: 160,
          borderRadius: "50%",
          background: "rgba(135,184,230,.14)",
          pointerEvents: "none",
          ...(photo ? { zIndex: 1 } : null),
        }}
      />
      {/* Espace « haut » de la maquette (118 px de photo visible au-dessus du
          contenu) — uniquement avec photo. */}
      {photo ? <div aria-hidden="true" style={{ height: 118 }} /> : null}
      {photo ? (
        <div style={{ position: "relative", zIndex: 2 }}>{contenu}</div>
      ) : (
        contenu
      )}
    </div>
  );
}

/**
 * LE FIL — une colonne, et c'est tout le correctif.
 *
 * Chaque étape : une pastille de 26 px, un filet vertical, le texte. Aucune
 * largeur n'est dictée par un mot : rien ne peut plus déborder de 390 px.
 */
export function Fil({ modele, gestes, arrive, contratHref = null }) {
  return (
    <div data-testid="sejour-fil" style={{ paddingLeft: 2 }}>
      {modele.ordre.map((cle, i) => (
        <Etape
          key={cle}
          cle={cle}
          rang={i + 1}
          statut={modele.etapes[cle]}
          roi={modele.roi === cle}
          dernier={i === modele.ordre.length - 1}
          gestes={gestes}
          arrive={arrive}
          contratHref={contratHref}
          cautionOuvreLe={modele.cautionOuvreLe}
          manque={modele.manque}
          sansPieceIdentite={modele.sansPieceIdentite}
          modele={modele}
        />
      ))}
    </div>
  );
}

/**
 * [D33 + arrêt 3, 2026-09-05] L'ÉTAPE 5 ÉNUMÈRE CE QUI MANQUE VRAIMENT.
 * La phrase de la maquette (« Vos instructions d'arrivée arrivent une fois la
 * caution et le formulaire faits. ») suit la liste réelle des manques — jamais
 * un générique « votre préparation terminée », jamais un manquant passé sous
 * silence, jamais une caution citée sur un parcours qui n'en a pas (Airbnb).
 * ORDRE DU SERVEUR, PAS DE LA MAQUETTE (correction 29/09) : le serveur refuse
 * la caution tant que le formulaire manque (`caution_form_required`) — le
 * texte dit donc le formulaire d'abord. Aucun des deux dans la liste
 * (cas structurellement inatteignable : signature/paiement seuls restants
 * alors que formulaire ET caution seraient faits) → repli générique existant.
 */
function attenteConfirmation(manque) {
  const items = [];
  if (Array.isArray(manque) && manque.includes("formulaire"))
    items.push("le formulaire");
  if (Array.isArray(manque) && manque.includes("caution"))
    items.push("la caution");
  if (items.length === 2)
    return "Vos instructions d’arrivée arrivent une fois votre formulaire rempli, puis la caution déposée.";
  if (items.length === 1) {
    return items[0] === "la caution"
      ? "Vos instructions d’arrivée arrivent une fois la caution faite."
      : "Vos instructions d’arrivée arrivent une fois le formulaire fait.";
  }
  return ETAPES.confirmation.attente;
}

function Etape({
  cle,
  rang,
  statut,
  roi,
  dernier,
  gestes,
  contratHref = null,
  arrive,
  cautionOuvreLe,
  manque = null,
  sansPieceIdentite = false,
  modele = null,
}) {
  const T = textesEtape(cle, modele);
  const fait = statut === "done";
  const titre = cle === "confirmation" && arrive ? T.titreArrive : T.titre;

  let description;
  if (cle === "confirmation") {
    description = arrive
      ? fait
        ? T.doneArrive
        : // Arrivé mais pas en règle : la phrase courte — le livret est
          // verrouillé, sa carte porte seule le message (D4/arrêt 8).
          manque.length === 0
          ? T.attenteArriveToutRegle
          : T.attenteArrive
      : fait
        ? T.done
        : // [D33] la phrase suit ce qui manque VRAIMENT — cf. attenteConfirmation.
          attenteConfirmation(manque);
  } else if (fait) {
    description = T.done;
  } else if (statut === "active") {
    // [maquette v4, activeAirbnb] Airbnb : « Heure d'arrivée et coordonnées. »
    // — pas de pièce d'identité, on ne promet pas ce qu'on ne demande pas.
    description =
      cle === "formulaire" && sansPieceIdentite
        ? "Heure d’arrivée et coordonnées."
        : T.active;
  } else if (statut === "attente_formulaire") {
    description = T.attente_formulaire;
  } else if (cle === "caution" && statut === "attente_fenetre") {
    // La date d'ouverture est servie MÊME quand la caution est fermée : c'est
    // tout l'intérêt. Sans elle, la formule générique — jamais un clic dans le vide.
    description = cautionOuvreLe
      ? `La caution sera disponible à partir du ${formatJourMois(cautionOuvreLe)}.`
      : "Disponible 2 jours avant votre arrivée.";
  } else {
    description = T.attente || T.active;
  }

  const geste = gestes && gestes[cle];

  const pastille = (
    <span
      style={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11.5,
        fontWeight: 600,
        border: `1.5px solid ${fait ? C.vert : roi ? C.ciel : C.line}`,
        background: fait ? C.vert : roi ? C.ciel : "#fff",
        color: fait || roi ? "#fff" : C.inkSoft,
      }}
    >
      {fait ? "✓" : rang}
    </span>
  );

  return (
    <div
      data-testid={`sejour-etape-${cle}`}
      data-statut={statut}
      data-roi={roi ? "1" : "0"}
      style={{ display: "flex", gap: 12, position: "relative" }}
    >
      <span
        style={{
          width: 26,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {pastille}
        {dernier ? null : (
          <span
            style={{
              flex: 1,
              width: 2,
              background: fait ? C.vert : C.line,
              margin: "3px 0",
            }}
          />
        )}
      </span>
      <span style={{ flex: 1, minWidth: 0, paddingBottom: 16 }}>
        {roi ? (
          <CarteDuMoment
            cle={cle}
            titre={titre}
            description={description}
            geste={geste}
            modele={modele}
          />
        ) : (
          <>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                minHeight: 26,
              }}
            >
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: statut === "pending" ? C.inkSoft : C.ink,
                }}
              >
                {titre}
              </span>
              {fait ? (
                <span
                  style={{
                    fontSize: 11,
                    color: C.vertTx,
                    background: C.vertBg,
                    padding: "2px 8px",
                    borderRadius: 9,
                    fontWeight: 600,
                  }}
                >
                  {T.fait}
                </span>
              ) : null}
            </span>
            {/* [D5] Les autres étapes actives gardent leur geste — en ligne
                sobre, jamais un second bouton plein. Une étape active sans
                adresse n'affiche PAS de ligne cliquable. */}
            {statut === "active" && geste ? (
              <GesteEnLigne texte={description} geste={geste} cle={cle} />
            ) : (
              <span
                style={{
                  display: "block",
                  fontSize: 12.5,
                  color: C.inkSoft,
                  lineHeight: 1.55,
                  marginTop: 4,
                }}
              >
                {description}
              </span>
            )}
            {/* LE CONTRAT SIGNÉ RESTE RELISIBLE (Dimitri 2026-09-05). Bouton,
                pas lien : « un lien ne se touche pas au pouce ». Sans adresse
                servie, RIEN — jamais un bouton qui n'ouvre pas le document. */}
            {cle === "signature" && fait && contratHref ? (
              <a
                data-testid="sejour-contrat-relire"
                href={contratHref}
                target="_blank"
                rel="noreferrer"
                style={{
                  ...CIBLE,
                  display: "inline-flex",
                  justifyContent: "center",
                  marginTop: 9,
                  padding: "0 14px",
                  minHeight: 44,
                  borderRadius: 12,
                  background: C.cielBg,
                  border: `1.5px solid ${C.ciel}`,
                  color: C.encre,
                  fontSize: 13.5,
                  fontWeight: 600,
                  fontFamily: F.sans,
                  textDecoration: "none",
                }}
              >
                ↓ Télécharger mon contrat
              </a>
            ) : null}
          </>
        )}
      </span>
    </div>
  );
}

function GesteEnLigne({ texte, geste, cle }) {
  const contenu = (
    <>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          color: C.inkSoft,
          lineHeight: 1.45,
        }}
      >
        {texte}
      </span>
      <span style={{ fontSize: 15, color: C.ciel, flexShrink: 0 }}>›</span>
    </>
  );
  const style = {
    ...CIBLE,
    gap: 8,
    width: "100%",
    textAlign: "left",
    marginTop: 3,
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: F.sans,
    textDecoration: "none",
    cursor: "pointer",
  };
  return geste.href ? (
    <a data-testid={`sejour-geste-${cle}`} href={geste.href} style={style}>
      {contenu}
    </a>
  ) : (
    <button
      data-testid={`sejour-geste-${cle}`}
      type="button"
      onClick={geste.onClick}
      style={style}
    >
      {contenu}
    </button>
  );
}

/** [D5] LE geste du moment : une seule carte, un seul bouton plein.
 *
 * Le geste peut porter, en plus de `href`/`onClick` :
 *   · `contenu`  — un bloc rendu ENTRE la description et le bouton. C'est par
 *     là que la signature amène son contrat (extrait, « lire en entier »,
 *     case « Lu et approuvé ») : la carte reste la même pour toutes les
 *     étapes, seul le contenu du moment change.
 *   · `desarme`  — le bouton est rendu mais DÉSARMÉ, et ça se voit (opacité,
 *     `disabled`). Réservé aux gestes à prérequis (la case à cocher de la
 *     signature) : un bouton qui a l'air actif et refuse est pire qu'un
 *     bouton gris.
 *   · `libelle`  — remplace le libellé canonique (l'état d'envoi :
 *     « Signature… »).
 *   · `erreur`   — un bloc rendu SOUS le bouton (l'échec dit à l'écran).
 */
function CarteDuMoment({ cle, titre, description, geste, etiquette, modele = null }) {
  const T = textesEtape(cle, modele);
  return (
    <div
      data-testid="sejour-carte-du-moment"
      data-etape={cle}
      style={{
        background: "#fff",
        borderRadius: 15,
        padding: 15,
        marginTop: 2,
        boxShadow: `0 3px 16px rgba(135,184,230,.22), 0 0 0 1.5px ${C.ciel}`,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: 24,
          padding: "0 11px",
          borderRadius: 12,
          background: C.ciel,
          color: C.bleu,
          fontSize: 10.5,
          fontWeight: 600,
          marginBottom: 11,
        }}
      >
        {etiquette || "À vous de jouer"}
      </span>
      <b
        style={{
          display: "block",
          fontFamily: F.serif,
          fontSize: 17,
          fontWeight: 600,
          lineHeight: 1.3,
        }}
      >
        {titre}
      </b>
      <p
        style={{
          fontSize: 13,
          color: C.inkSoft,
          lineHeight: 1.55,
          margin: "5px 0 0",
        }}
      >
        {description}
      </p>
      {geste && geste.contenu ? geste.contenu : null}
      {/* [arrêt 3, 2026-09-05] L'encadré de rassurance (maquette `.rea`) —
          aujourd'hui seul « payer le séjour » en porte un. Après le prix,
          avant le bouton : l'ordre du prototype. */}
      {T.rea ? (
        <div
          data-testid={`sejour-rea-${cle}`}
          style={{
            display: "flex",
            gap: 9,
            background: C.cielBg,
            borderRadius: 11,
            padding: "10px 12px",
            fontSize: 12,
            color: C.bleu,
            lineHeight: 1.5,
            marginTop: 11,
          }}
        >
          {T.rea}
        </div>
      ) : null}
      {geste ? (
        <div style={{ marginTop: 13 }}>
          <BoutonPlein
            testId={`sejour-cta-${cle}`}
            href={geste.href}
            onClick={geste.onClick}
            disabled={geste.desarme}
            sous={T.sous}
          >
            {geste.libelle || T.geste}
          </BoutonPlein>
        </div>
      ) : null}
      {geste && geste.erreur ? geste.erreur : null}
    </div>
  );
}

/** Les grandes cartes du séjour : livret, services. */
export function GrandeCarte({
  icone,
  titre,
  sous,
  href,
  onClick,
  eteinte,
  testId,
}) {
  const contenu = (
    <>
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: 14,
          background: eteinte ? C.tuileFond : C.ciel,
          color: eteinte ? C.inkSoft : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 19,
        }}
        aria-hidden="true"
      >
        {icone}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <b
          style={{
            display: "block",
            fontSize: 15.5,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {titre}
        </b>
        <span
          style={{
            display: "block",
            fontSize: 12.5,
            color: C.inkSoft,
            marginTop: 4,
            lineHeight: 1.45,
          }}
        >
          {sous}
        </span>
      </span>
      {eteinte ? null : (
        <span style={{ fontSize: 18, color: C.inkSoft, flexShrink: 0 }}>›</span>
      )}
    </>
  );
  const style = {
    display: "flex",
    alignItems: "center",
    gap: 13,
    width: "100%",
    textAlign: "left",
    background: "#fff",
    borderRadius: 17,
    padding: 16,
    marginBottom: 10,
    minHeight: 86,
    border: "none",
    fontFamily: F.sans,
    color: C.ink,
    textDecoration: "none",
    boxShadow: "0 2px 10px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.05)",
    opacity: eteinte ? 0.62 : 1,
  };
  // Éteinte = aucune cible : une carte grisée cliquable est un bouton mort.
  if (eteinte) {
    return (
      <div data-testid={testId} data-eteinte="1" style={style}>
        {contenu}
      </div>
    );
  }
  return href ? (
    <a data-testid={testId} href={href} style={style}>
      {contenu}
    </a>
  ) : (
    <button data-testid={testId} type="button" onClick={onClick} style={style}>
      {contenu}
    </button>
  );
}

/** Le volet replié « Votre préparation (n/N) ». */
export function VoletPreparation({
  modele,
  gestes,
  arrive,
  ouvert,
  onBascule,
}) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 15,
        marginBottom: 10,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.05)",
      }}
    >
      <button
        type="button"
        data-testid="sejour-volet-preparation"
        aria-expanded={ouvert ? "true" : "false"}
        onClick={onBascule}
        style={{
          ...CIBLE,
          gap: 12,
          width: "100%",
          textAlign: "left",
          padding: "13px 14px",
          minHeight: 56,
          background: "none",
          border: "none",
          fontFamily: F.sans,
          color: C.ink,
          cursor: "pointer",
        }}
      >
        <b style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>
          Votre préparation ({modele.anneau.faites}/{modele.anneau.total})
        </b>
        <span
          style={{
            fontSize: 16,
            color: C.inkSoft,
            flexShrink: 0,
            transform: ouvert ? "rotate(90deg)" : "none",
          }}
        >
          ›
        </span>
      </button>
      {ouvert ? (
        <div style={{ padding: "2px 14px 14px" }}>
          <Fil modele={modele} gestes={gestes} arrive={arrive} />
        </div>
      ) : null}
    </div>
  );
}

/** [D23] Le signalement — sous les services, à part du livret. */
export function BlocSignaler({ onProbleme, onCasse }) {
  const entrees = [
    {
      icone: "⚠",
      titre: "Un problème",
      sous: "chauffage, wifi, équipement…",
      onClick: onProbleme,
    },
    {
      icone: "◌",
      titre: "Une casse ou un dégât",
      sous: "photo demandée",
      onClick: onCasse,
    },
  ].filter((e) => !!e.onClick);
  if (!entrees.length) return null;
  return (
    <div
      data-testid="sejour-bloc-signaler"
      style={{
        background: "#fff",
        borderRadius: 17,
        overflow: "hidden",
        marginBottom: 10,
        boxShadow:
          "0 2px 10px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.05)",
      }}
    >
      <div style={{ padding: "16px 16px 0" }}>
        <b
          style={{
            display: "block",
            fontFamily: F.serif,
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          Quelque chose à nous dire ?
        </b>
        {/* [D23] « Le ton invite, il n'accuse pas — un voyageur qui craint la
            punition se tait. » */}
        <p
          style={{
            fontSize: 12.5,
            color: C.inkSoft,
            margin: "5px 0 0",
            lineHeight: 1.55,
          }}
        >
          Nous préférons toujours le savoir, quoi qu’il arrive.
        </p>
      </div>
      <div style={{ padding: "13px 16px 0" }}>
        {entrees.map((e, i) => (
          <button
            key={e.titre}
            type="button"
            data-testid={`sejour-signaler-${i === 0 ? "probleme" : "casse"}`}
            onClick={e.onClick}
            style={{
              ...CIBLE,
              gap: 13,
              width: "100%",
              textAlign: "left",
              minHeight: 60,
              padding: "11px 0",
              borderTop: i ? `1px solid ${C.line}` : "none",
              borderLeft: "none",
              borderRight: "none",
              borderBottom: "none",
              background: "none",
              fontFamily: F.sans,
              color: C.ink,
              cursor: "pointer",
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                background: C.cielBg,
                color: C.bleu,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 15,
              }}
              aria-hidden="true"
            >
              {e.icone}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  fontSize: 13.5,
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {e.titre}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: 11.5,
                  color: C.inkSoft,
                  marginTop: 2,
                }}
              >
                {e.sous}
              </span>
            </span>
            <span style={{ fontSize: 17, color: C.inkSoft, flexShrink: 0 }}>
              ›
            </span>
          </button>
        ))}
      </div>
      {/* [D24] ⚠️ On dit « votre envoi est daté », JAMAIS « vos photos sont
          horodatées » : la compression efface les données de l'appareil, et
          celles-ci se falsifient en trois secondes dans les réglages du
          téléphone. Seule la date du serveur prouve quelque chose. */}
      <div
        style={{
          display: "flex",
          gap: 10,
          background: C.cielBg,
          padding: "13px 16px",
          marginTop: 14,
        }}
      >
        <span
          style={{ color: C.bleu, flexShrink: 0, fontSize: 13 }}
          aria-hidden="true"
        >
          ✓
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 11.5,
            color: C.bleu,
            lineHeight: 1.55,
          }}
        >
          Le dire maintenant vous protège : nous saurons que ce n’était pas de
          votre fait, ou que vous nous l’avez dit vous-même. Votre envoi est
          daté.
        </span>
      </div>
    </div>
  );
}

/** [D7] Le contact, une fois par écran — sobre, sans numéro affiché [D6]. */
/**
 * BOUTON SIMPLE « Une question ? Écrivez-nous ».
 *
 * RELEVÉ DES BLOCS (2026-09-05) : le prototype de Dimitri n'a PAS le même
 * contact selon le moment. Avant l'arrivée, au départ et en J+30, c'est CE
 * bouton — sobre, en dernier. Pendant le séjour SEULEMENT, c'est le bloc riche
 * à deux boutons ci-dessous. Le montage avait uniformisé le bloc riche partout,
 * et l'avait placé trop haut. Deux traitements, deux moments.
 */
export function BoutonJoindre({ email }) {
  if (!email) return null;
  return (
    <a
      data-testid="sejour-bouton-joindre"
      href={`mailto:${email}`}
      style={{
        ...CIBLE,
        display: "flex",
        justifyContent: "center",
        gap: 8,
        width: "100%",
        minHeight: 48,
        marginBottom: 11,
        borderRadius: 12,
        background: C.cielBg,
        border: `1.5px solid ${C.ciel}`,
        color: C.encre,
        fontSize: 14,
        fontWeight: 600,
        fontFamily: F.sans,
        textDecoration: "none",
      }}
    >
      ☎ Une question ? Écrivez-nous
    </a>
  );
}

export function BlocJoindre({ email, telephone = null }) {
  // LE BLOC « NOUS JOINDRE » — SPÉCIFICATION DE DIMITRI (2026-09-05), au mot :
  // « Besoin de quelque chose ? » · « Par message ou par téléphone, on répond
  // vite » · DEUX boutons CARRÉS de 44 px à droite, une enveloppe et un
  // téléphone. Le montage n'en avait qu'un, libellé « Écrire ».
  //
  // Chaque bouton est porté par SA donnée : pas d'email ⇒ pas d'enveloppe,
  // pas de numéro ⇒ pas de téléphone. Aucun des deux ⇒ pas de bloc du tout.
  // Un bouton qui n'appelle personne serait pire que l'absence de bouton.
  if (!email && !telephone) return null;
  const carre = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    // minHeight explicite : l'invariant « toute cible tactile fait au moins
    // 44 px » lit CETTE propriété. Sans elle il mesure NaN — et un invariant
    // qui mesure NaN ne protège plus rien.
    minHeight: 44,
    flexShrink: 0,
    borderRadius: 12,
    background: C.cielBg,
    border: `1.5px solid ${C.ciel}`,
    color: C.encre,
    fontSize: 17,
    textDecoration: "none",
  };
  return (
    <div
      data-testid="sejour-bloc-joindre"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: "#fff",
        borderRadius: 15,
        padding: 15,
        marginBottom: 11,
        boxShadow:
          "0 1px 3px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.06)",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <b
          style={{
            display: "block",
            fontFamily: F.serif,
            fontSize: 15,
            fontWeight: 600,
            color: C.ink,
            lineHeight: 1.3,
          }}
        >
          Besoin de quelque chose&nbsp;?
        </b>
        <span
          style={{
            display: "block",
            fontSize: 12.5,
            color: C.inkSoft,
            lineHeight: 1.5,
            marginTop: 3,
          }}
        >
          Par message ou par téléphone, on répond vite
        </span>
      </span>
      {email ? (
        <a
          data-testid="sejour-joindre-email"
          href={`mailto:${email}`}
          aria-label="Nous écrire"
          title="Nous écrire"
          style={carre}
        >
          ✉
        </a>
      ) : null}
      {telephone ? (
        <a
          data-testid="sejour-joindre-tel"
          href={`tel:${String(telephone).replace(/[^+0-9]/g, "")}`}
          aria-label="Nous appeler"
          title="Nous appeler"
          style={carre}
        >
          ☎
        </a>
      ) : null}
    </div>
  );
}

export function BlocAttenteCode({ quand, instructionsUrl }) {
  return (
    <div
      data-testid="sejour-attente-code"
      style={{
        display: "flex",
        gap: 12,
        background: "#fff",
        borderRadius: 15,
        padding: 14,
        marginBottom: 12,
        boxShadow: `0 1px 3px rgba(35,50,64,.07), 0 0 0 1px ${C.ciel}`,
      }}
    >
      <span
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: C.cielBg,
          color: C.bleu,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 15,
        }}
        aria-hidden="true"
      >
        ◷
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <b
          style={{
            display: "block",
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          Votre code d’entrée arrive {quand}
        </b>
        <span
          style={{
            display: "block",
            fontSize: 12.5,
            color: C.inkSoft,
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          Il apparaîtra ici, et vous le recevrez aussi par message avec les
          instructions d’arrivée.
        </span>
        {instructionsUrl ? (
          <a
            data-testid="sejour-lien-instructions"
            href={instructionsUrl}
            style={{
              ...CIBLE,
              marginTop: 6,
              fontSize: 12.5,
              fontWeight: 600,
              color: C.bleu,
              textDecoration: "underline",
            }}
          >
            Voir les instructions d’arrivée
          </a>
        ) : null}
      </span>
    </div>
  );
}

// ── LE BLOC D'ARRIVÉE (v4.1 — D35/D36, Dimitri 2026-09-05) ──────────────────
//
// « La vidéo, le code, les instructions — dans cet ordre, en un seul bloc
// navy. On lit du haut vers le bas : je regarde, j'ai le code, je sais où
// aller. Le code ne demande jamais un clic de plus. » (maquette v4.1,
// blocCode). Et [D36] : « la vidéo ET les instructions viennent de la fiche
// instructions d'arrivée du livret. Une seule source — je remplis la fiche,
// l'écran suit. »
//
// PRÉSENTATION PURE : le composant reçoit `code_acces` DÉJÀ gaté par le
// serveur (double condition : conditions de l'envoi ∧ moment — la fiche ne
// s'y ajoute pas, elle n'en retire rien). Il montre CE QUI EXISTE :
//   · LA VIDÉO en tête (fiche_arrivee.video_url → videoUrlToEmbed, LE même
//     convertisseur que le livret) — pas de vidéo ⇒ pas de lecteur.
//     ⚠️ LA DURÉE N'EXISTE PAS EN DONNÉE : l'emplacement est prêt
//     (`video_duree`, cf. sous-titre) mais RIEN n'est deviné — sans elle, le
//     sous-titre de la maquette sans le chiffre.
//   · LE CODE en grand (box_code, sinon interphone en vedette), « ⧉ Copier »
//     (libellé v4.1) — jamais un faux « 0000 ».
//   · LES INSTRUCTIONS sous un filet : le CONTENU de la fiche, rendu par LES
//     helpers du livret (pickLang fr, sanitize HTML / markdown — on ne parse
//     JAMAIS du texte libre en étapes inventées : une liste numérotée de la
//     fiche se rend numérotée, un paragraphe reste un paragraphe). Fiche
//     vide ⇒ REPLI sur les deux champs de config `instructions_acces_*`
//     (c'est le sens de « une seule source » : la fiche d'abord, la config
//     en filet de sécurité).
//   · L'ITINÉRAIRE ET L'ADRESSE en pied — l'adresse vient de la cfg
//     (`adresse_complete`, servie `adresse`) ; l'itinéraire est un LIEN de
//     guidage (motif maison https://www.google.com/maps/search/?api=1&query=,
//     déjà utilisé par l'inbox — une URL, pas une API). Pas d'adresse ⇒ pas
//     de pied du tout.
// Si RIEN n'existe, le composant ne rend rien (hasCodeAccesContent) — jamais
// un bloc vide.
export function hasCodeAccesContent(code) {
  return !!(
    code &&
    (code.box_code ||
      code.interphone_code ||
      code.instructions_acces_batiment ||
      code.instructions_acces_appartement ||
      (code.fiche_arrivee &&
        (code.fiche_arrivee.video_url || code.fiche_arrivee.content)) ||
      code.adresse)
  );
}

// Styles du contenu de fiche (texte riche rendu dans le navy) — scopés par
// classe : les instructions numérotées de la fiche prennent le dessin de la
// maquette (numéro ciel à gauche, texte clair), sans jamais réécrire le
// contenu lui-même.
const FICHE_ARRIVEE_CSS = `
.fbzs-fiche{font-size:12.5px;color:rgba(255,255,255,.82);line-height:1.5}
.fbzs-fiche p{margin:6px 0}
.fbzs-fiche a{color:${C.ciel}}
.fbzs-fiche ol{list-style:none;counter-reset:fbzs-etape;margin:0;padding:0}
.fbzs-fiche ol li{counter-increment:fbzs-etape;display:flex;gap:11px;padding:6px 0}
.fbzs-fiche ol li::before{content:counter(fbzs-etape);color:${C.ciel};font-size:13px;flex-shrink:0;line-height:1.5}
.fbzs-fiche ul{margin:6px 0;padding-left:18px}
.fbzs-fiche ul li{padding:3px 0}
.fbzs-fiche img{max-width:100%;border-radius:8px}
`;

export function BlocCodeAcces({ code }) {
  const [copie, setCopie] = React.useState(false);
  const [adresseCopiee, setAdresseCopiee] = React.useState(false);
  const [videoOuverte, setVideoOuverte] = React.useState(false);
  if (!hasCodeAccesContent(code)) return null;

  // La vedette : le code de la boîte à clés, sinon l'interphone.
  const vedette = code.box_code
    ? { libelle: "Le code de la boîte à clés", valeur: code.box_code }
    : code.interphone_code
      ? { libelle: "Le code de l’interphone", valeur: code.interphone_code }
      : null;
  const interphoneSecondaire = code.box_code && code.interphone_code;

  // [D36] La fiche du livret — vidéo + contenu, rendus par LES helpers du
  // livret. Fail-soft champ par champ.
  const fiche = code.fiche_arrivee || null;
  const videoEmbed = fiche ? videoUrlToEmbed(fiche.video_url) : null;
  // ⚠️ EMPLACEMENT PRÊT, DONNÉE ABSENTE : la durée de la vidéo n'existe nulle
  // part en base aujourd'hui. Le jour où le serveur la sert (`video_duree`),
  // elle s'insère devant le sous-titre (« 1 min 20 · de la rue à votre
  // porte », maquette v4.1) — d'ici là, JAMAIS une durée devinée.
  const videoDuree =
    fiche && typeof fiche.video_duree === "string" && fiche.video_duree.trim()
      ? fiche.video_duree.trim()
      : null;
  const ficheHtml = (() => {
    if (!fiche || !fiche.content) return null;
    const brut = pickLang(fiche.content, "fr", "fr").value || "";
    if (!brut.trim()) return null;
    return fiche.content_type === "html"
      ? sanitizeLivretHtml(brut)
      : parseLivretMarkdown(brut);
  })();
  // REPLI config (« une seule source ») : les deux champs texte ne s'affichent
  // que si la fiche n'a pas de contenu.
  const accesConfig = ficheHtml
    ? []
    : [
        code.instructions_acces_batiment,
        code.instructions_acces_appartement,
      ].filter(Boolean);

  // Le pied — seulement si l'adresse existe (jamais un lien de guidage vide).
  const adresse =
    typeof code.adresse === "string" && code.adresse.trim()
      ? code.adresse.trim()
      : null;
  const itineraireHref = adresse
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(adresse)}`
    : null;

  const copierTexte = (texte, setFlag) => {
    try {
      if (navigator.clipboard && texte) {
        navigator.clipboard.writeText(texte);
        setFlag(true);
        setTimeout(() => setFlag(false), 2000);
      }
    } catch (_) {
      // presse-papiers indisponible : tout reste lisible à l'écran
    }
  };

  const boutonPiedStyle = {
    flex: 1,
    minHeight: 44,
    borderRadius: 12,
    border: "none",
    background: "rgba(255,255,255,.14)",
    color: "#fff",
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: F.sans,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
  };

  return (
    <div
      data-testid="sejour-code-acces"
      style={{
        background: C.sejourFond,
        borderRadius: 18,
        marginBottom: 12,
        overflow: "hidden",
        boxShadow: "0 3px 16px rgba(35,50,64,.18)",
      }}
    >
      <style>{FICHE_ARRIVEE_CSS}</style>

      {/* 1 · LA VIDÉO EN TÊTE — « on la voit avant de lire quoi que ce soit ».
          L'aperçu de la maquette (cercle de lecture, titre, sous-titre) ; un
          clic charge l'embed — LE même iframe sandboxé que le livret. */}
      {videoEmbed ? (
        videoOuverte ? (
          <div
            data-testid="sejour-code-video-lecteur"
            style={{ position: "relative", aspectRatio: "16/9" }}
          >
            <iframe
              title="Comment entrer"
              src={videoEmbed}
              sandbox="allow-scripts allow-same-origin allow-presentation"
              loading="lazy"
              allow="autoplay; fullscreen"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            data-testid="sejour-code-video"
            onClick={() => setVideoOuverte(true)}
            style={{
              display: "block",
              width: "100%",
              position: "relative",
              aspectRatio: "16/9",
              background: "linear-gradient(135deg,#2c3f52,#1a2733)",
              overflow: "hidden",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at 30% 40%,rgba(135,184,230,.16),transparent 60%)",
              }}
            />
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                width: 58,
                height: 58,
                borderRadius: "50%",
                background: "rgba(255,255,255,.94)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 20px rgba(0,0,0,.3)",
              }}
            >
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: `17px solid ${C.sejourFond}`,
                  borderTop: "11px solid transparent",
                  borderBottom: "11px solid transparent",
                  marginLeft: 5,
                }}
              />
            </span>
            <span
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                padding: "13px 15px",
                textAlign: "left",
                background: "linear-gradient(transparent,rgba(26,39,51,.88))",
              }}
            >
              <span
                style={{
                  display: "block",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#fff",
                  lineHeight: 1.3,
                }}
              >
                Comment entrer
              </span>
              {/* Pas de sous-titre (verdict Dimitri 2026-09-05 : « Comment entrer »,
                  rien de plus). Si un jour la durée est saisie, elle s'affiche seule ici. */}
              {videoDuree ? (
                <span
                  style={{
                    display: "block",
                    fontSize: 11.5,
                    color: "rgba(255,255,255,.75)",
                    marginTop: 3,
                  }}
                >
                  {videoDuree}
                </span>
              ) : null}
            </span>
          </button>
        )
      ) : null}

      {/* 2 · LE CODE, juste dessous — il ne demande jamais un clic de plus. */}
      <div style={{ padding: "16px 17px 17px", position: "relative" }}>
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: -45,
            left: -25,
            width: 130,
            height: 130,
            borderRadius: "50%",
            background: "rgba(135,184,230,.11)",
          }}
        />
        {vedette ? (
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 13,
              position: "relative",
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "rgba(255,255,255,.66)",
                }}
              >
                {vedette.libelle}
              </span>
              <b
                data-testid="sejour-code-principal"
                style={{
                  display: "block",
                  fontSize: 36,
                  fontWeight: 600,
                  color: "#fff",
                  letterSpacing: ".12em",
                  lineHeight: 1.15,
                  marginTop: 4,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {vedette.valeur}
              </b>
            </span>
            <button
              type="button"
              data-testid="sejour-code-copier"
              onClick={() => copierTexte(vedette.valeur, setCopie)}
              style={{
                minHeight: 44,
                padding: "0 14px",
                borderRadius: 12,
                border: "none",
                background: C.ciel,
                color: C.sejourFond,
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: F.sans,
                flexShrink: 0,
                cursor: "pointer",
              }}
            >
              {copie ? "✓ Copié" : "⧉ Copier"}
            </button>
          </div>
        ) : (
          // Aucun code numérique — le bloc porte alors le reste (vidéo,
          // instructions, adresse) sous un titre sobre.
          <span
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              position: "relative",
            }}
          >
            Votre accès au logement
          </span>
        )}
        {interphoneSecondaire ? (
          <span
            data-testid="sejour-code-interphone"
            style={{
              display: "block",
              fontSize: 12,
              color: "rgba(255,255,255,.76)",
              marginTop: 6,
              position: "relative",
            }}
          >
            Interphone&nbsp;: {code.interphone_code}
          </span>
        ) : null}

        {/* 3 · LES INSTRUCTIONS, séparées par un filet — la fiche du livret
            d'abord [D36] ; la config en repli. Rien ⇒ pas de section. */}
        {ficheHtml || accesConfig.length ? (
          <div
            data-testid="sejour-code-instructions"
            style={{
              marginTop: 15,
              paddingTop: 14,
              borderTop: "1px solid rgba(255,255,255,.14)",
              position: "relative",
            }}
          >
            {ficheHtml ? (
              <div
                className="fbzs-fiche"
                // Sanitisé par LES helpers du livret (sanitizeLivretHtml /
                // parseLivretMarkdown — les deux sortent un HTML sûr).
                dangerouslySetInnerHTML={{ __html: ficheHtml }}
              />
            ) : (
              accesConfig.map((txt, i) => (
                <span
                  key={i}
                  style={{
                    display: "block",
                    fontSize: 12.5,
                    color: "rgba(255,255,255,.82)",
                    marginTop: i ? 8 : 0,
                    lineHeight: 1.5,
                    whiteSpace: "pre-line",
                  }}
                >
                  {txt}
                </span>
              ))
            )}
          </div>
        ) : null}

        {/* 4 · L'ITINÉRAIRE ET L'ADRESSE en pied — seulement si l'adresse
            existe (un guidage sans adresse serait un bouton mort). */}
        {adresse ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 14,
              position: "relative",
            }}
          >
            <a
              data-testid="sejour-code-itineraire"
              href={itineraireHref}
              target="_blank"
              rel="noreferrer"
              style={boutonPiedStyle}
            >
              ↗ L’itinéraire
            </a>
            <button
              type="button"
              data-testid="sejour-code-adresse"
              onClick={() => copierTexte(adresse, setAdresseCopiee)}
              style={boutonPiedStyle}
            >
              {adresseCopiee ? "✓ Copiée" : "⧉ L’adresse"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── LES BLOCS DU DÉPART ─────────────────────────────────────────────────────
//
// Le dessin du départ (Dimitri, 2026-09-04) : « la carte de remerciement avec
// le lotus en cercle, le reçu replié en une ligne qu'on déplie, l'avis dans son
// propre bloc avec "il aide les voyageurs qui hésitent", et le code promo en
// bloc navy avec −10 % en grand ».
//
// ═══ POURQUOI CES BLOCS VIVENT ICI, ET PAS DANS LES DEUX PAGES ═══
// Ce sont des composants de PRÉSENTATION PURE : ils reçoivent des chaînes DÉJÀ
// résolues (donc déjà traduites, par le dictionnaire de chaque page) et des
// adresses DÉJÀ null-gatées. Aucun d'eux ne lit la charge, aucun ne connaît le
// parcours. C'est ce qui garantit que le départ Direct et le départ plateformes
// ont LE MÊME dessin : il n'y a qu'un seul rendu, et l'ordre des blocs est
// écrit une seule fois (`BlocsDepart`). Le montage précédent avait deux écrans
// de départ recopiés — c'est ainsi qu'ils avaient fini par diverger.
//
// ═══ CE QUE CES BLOCS NE FONT JAMAIS ═══
//   · Calculer. Aucun montant, aucune remise, aucun total n'est dérivé ici : on
//     met en forme ce que le serveur a servi. Le reçu ne se VENTILE PAS
//     (« 4 nuits × 90 € ») — cette pièce n'existe pas, cf. l'en-tête de
//     `SejourV2Ecran`.
//   · Rendre un bouton mort. Chaque bloc est null-gaté par son appelant ET
//     re-teste ses propres données : sans reçu, sans avis, sans code, le bloc
//     DISPARAÎT — il ne s'affiche pas vide, il ne montre pas « — ».
// ────────────────────────────────────────────────────────────────────────────

/**
 * La carte de remerciement — le lotus DANS UN CERCLE, puis la phrase.
 *
 * ⚠️ LOGO COULEUR, PAS LA VARIANTE BLANCHE — même raison que la ligne de marque
 * du bandeau (corrigée sur écran vivant le 2026-09-04) : la variante blanche
 * est le lotus SANS son disque. C'est le disque qui fait « le lotus en cercle » ;
 * la peindre au pochoir n'aurait rendu que des traits flottants dans un rond
 * pâle. Le halo ciel derrière le pose, il ne le remplace pas.
 *
 * [D9] AUCUNE MENTION DE CAUTION ici : la libération n'est pas décidée à ce
 * moment-là, le logement se vérifie d'abord. C'est un remerciement, pas un
 * point administratif.
 */
export function CarteRemerciement({ texte }) {
  if (!texte) return null;
  return (
    <div
      data-testid="depart-remerciement"
      style={{
        background: "#fff",
        borderRadius: 15, // MESURÉ au prototype (le montage était à 18).
        padding: 15, // MESURÉ au prototype (le montage était à 21/17/19).
        marginBottom: 11,
        textAlign: "center",
        boxShadow:
          "0 2px 12px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.05)",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 74,
          height: 74,
          borderRadius: "50%",
          background: C.cielBg,
          marginBottom: 13,
        }}
      >
        <img
          src={`data:image/png;base64,${LOGO_B64}`}
          alt=""
          aria-hidden="true"
          style={{ width: 52, height: 52, display: "block" }}
        />
      </span>
      <p
        style={{
          fontSize: 13,
          color: C.inkSoft,
          margin: 0,
          lineHeight: 1.6,
        }}
      >
        {texte}
      </p>
    </div>
  );
}

/**
 * LE REÇU — UNE LIGNE, QU'ON DÉPLIE.
 *
 * ⚠️ LE BOUTON DU DOCUMENT EST HORS DU REPLI, ET C'EST LE POINT ENTIER.
 * « Le cas de la note de frais deux mois après » : le justificatif doit rester
 * TOUJOURS ATTEIGNABLE. Le cacher derrière un dépliage l'aurait rendu
 * conditionnel à un geste — un clic de plus est un clic qu'on ne fait pas quand
 * on ne sait pas qu'il y a quelque chose derrière. Le repli ne porte donc que
 * le DÉTAIL ; la pièce, elle, est en clair.
 *
 * ⚠️ LE DÉTAIL EST CE QUE LE SERVEUR SERT, RIEN DE PLUS. Côté Direct : un
 * montant, les dates du séjour, le logement, une date de paiement. Il n'y a
 * AUCUNE ventilation (« 4 nuits × 90 € », « Ménage », « Taxe de séjour ») parce
 * qu'aucune n'est servie, et que la recalculer à l'écran produirait une pièce
 * qui peut diverger du document figé archivé à l'émission.
 *
 * @param {string}   titre         « Reçu de paiement »
 * @param {string}   vedette       la valeur lisible sans déplier (le montant) — facultative
 * @param {array}    lignes        [[étiquette, valeur], …] déjà formatées, déjà filtrées
 * @param {string}   voirDetail    libellé d'ouverture du repli
 * @param {string}   masquerDetail libellé de fermeture
 * @param {string}   documentHref  adresse du VRAI reçu — absente ⇒ pas de bouton
 * @param {string}   documentLabel libellé du bouton
 */
export function BlocRecuReplie({
  titre,
  vedette = null,
  lignes = [],
  voirDetail,
  masquerDetail,
  documentHref = null,
  documentLabel = null,
}) {
  const [ouvert, setOuvert] = React.useState(false);
  const propres = (Array.isArray(lignes) ? lignes : []).filter(
    (l) => Array.isArray(l) && l[0] && l[1],
  );
  // Ni détail, ni vedette, ni document ⇒ le bloc n'a rien à dire. Il disparaît.
  if (!propres.length && !vedette && !documentHref) return null;
  const depliable = propres.length > 0;
  // RIEN À DIRE EN TÊTE ⇒ PAS DE LIGNE D'EN-TÊTE. Sans titre, sans montant en
  // vedette et sans détail à déplier, la ligne ne portait plus qu'un vide au
  // dessus du bouton (canal plateformes — Dimitri 2026-09-04). Le bloc se
  // réduit alors à son bouton, ce qui est exactement ce qu'il est.
  const ligneEnTete = !!titre || !!vedette || depliable;

  return (
    <div
      data-testid="depart-recu"
      style={{
        background: "#fff",
        borderRadius: 15,
        padding: "4px 15px 13px",
        marginBottom: 11,
        boxShadow:
          "0 1px 3px rgba(35,50,64,.07), 0 0 0 1px rgba(35,50,64,.06)",
      }}
    >
      {/* LA LIGNE. Dépliable ⇒ c'est un bouton (44 px, aria-expanded) ; sans
          détail à montrer ⇒ c'est une simple ligne, jamais un bouton qui
          n'ouvrirait rien. RIEN À DIRE EN TÊTE ⇒ pas de ligne du tout : sans
          titre, sans montant en vedette et sans détail, elle ne portait qu'un
          vide au-dessus du bouton du document. */}
      {ligneEnTete && (depliable ? (
        <button
          data-testid="depart-recu-bascule"
          type="button"
          aria-expanded={ouvert}
          onClick={() => setOuvert((v) => !v)}
          style={{
            ...CIBLE,
            width: "100%",
            gap: 10,
            padding: "10px 0",
            background: "none",
            border: "none",
            font: "inherit",
            fontFamily: F.sans,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          {/* TITRE FACULTATIF (Dimitri 2026-09-04) : côté plateformes il n'y a
              rien à déplier, le bloc se réduit à son bouton — et le titre
              répétait mot pour mot le libellé de ce bouton. */}
          {titre ? (
            <b style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>
              {titre}
            </b>
          ) : null}
          {vedette ? (
            <b
              data-testid="depart-recu-vedette"
              style={{
                fontSize: 14.5,
                fontWeight: 700,
                color: C.bleu,
                marginLeft: "auto",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {vedette}
            </b>
          ) : null}
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0,
              marginLeft: vedette ? 0 : "auto",
              color: C.muet,
              fontSize: 12,
              transform: ouvert ? "rotate(180deg)" : "none",
            }}
          >
            ▾
          </span>
        </button>
      ) : (
        <div
          style={{
            ...CIBLE,
            gap: 10,
            padding: "10px 0",
            fontFamily: F.sans,
          }}
        >
          {/* TITRE FACULTATIF (Dimitri 2026-09-04) : côté plateformes il n'y a
              rien à déplier, le bloc se réduit à son bouton — et le titre
              répétait mot pour mot le libellé de ce bouton. */}
          {titre ? (
            <b style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>
              {titre}
            </b>
          ) : null}
          {vedette ? (
            <b
              data-testid="depart-recu-vedette"
              style={{
                fontSize: 14.5,
                fontWeight: 700,
                color: C.bleu,
                marginLeft: "auto",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {vedette}
            </b>
          ) : null}
        </div>
      ))}

      {depliable && ouvert ? (
        <dl
          data-testid="depart-recu-detail"
          style={{
            margin: "2px 0 6px",
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "7px 16px",
            fontSize: 13,
            lineHeight: 1.45,
            borderTop: `1px solid ${C.lineSoft}`,
            paddingTop: 12,
          }}
        >
          {propres.map(([etiquette, valeur]) => (
            <React.Fragment key={etiquette}>
              <dt style={{ margin: 0, color: C.inkSoft, fontWeight: 600 }}>
                {etiquette}
              </dt>
              <dd style={{ margin: 0, color: C.ink }}>{valeur}</dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}

      {depliable ? (
        <span
          aria-hidden="true"
          style={{
            display: "block",
            fontSize: 11.5,
            color: C.muet,
            marginBottom: documentHref ? 10 : 0,
          }}
        >
          {ouvert ? masquerDetail : voirDetail}
        </span>
      ) : null}

      {/* LE VRAI REÇU — hors du repli, toujours atteignable. Adresse absente
          ⇒ aucun bouton (le serveur ne sert pas encore ce document sur tous
          les parcours : un bouton mort serait pire que pas de bouton). */}
      {documentHref && documentLabel ? (
        <a
          className="fbzj-cta"
          data-testid="depart-recu-document"
          href={documentHref}
          target="_blank"
          rel="noreferrer"
          style={{
            ...CIBLE,
            justifyContent: "center",
            width: "100%",
            minHeight: 46,
            boxSizing: "border-box",
            borderRadius: 12,
            background: C.ciel,
            color: C.encre,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: F.sans,
            textDecoration: "none",
          }}
        >
          {documentLabel}
        </a>
      ) : null}
    </div>
  );
}

/**
 * L'AVIS — DANS SON PROPRE BLOC.
 *
 * « il aide les voyageurs qui hésitent » : la phrase de Dimitri est le
 * sous-texte du bloc, pas une accroche décorative — c'est la raison qu'on donne
 * au voyageur de prendre trente secondes.
 *
 * Deux formes selon le parcours, UN SEUL bloc : un lien (invitation Google,
 * côté Direct) ou un enfant interactif (le prompt ressenti 1–5, côté
 * plateformes). Ni lien ni enfant ⇒ le bloc disparaît.
 */
export function BlocAvis({ titre, aide, href = null, libelle = null, children = null }) {
  if (!href && !children) return null;
  return (
    <div
      data-testid="depart-avis"
      style={{
        background: "#fff",
        borderRadius: 15,
        padding: "15px 15px 16px",
        marginBottom: 11,
        boxShadow: `0 1px 3px rgba(35,50,64,.07), 0 0 0 1px ${C.ciel}`,
      }}
    >
      <b
        style={{
          display: "block",
          fontFamily: F.serif,
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.3,
          color: C.ink,
        }}
      >
        {titre}
      </b>
      <p
        style={{
          fontSize: 12.5,
          color: C.inkSoft,
          lineHeight: 1.55,
          margin: "5px 0 0",
        }}
      >
        {aide}
      </p>
      {children ? <div style={{ marginTop: 12 }}>{children}</div> : null}
      {href && libelle ? (
        <a
          className="fbzj-cta"
          data-testid="depart-avis-lien"
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{
            ...CIBLE,
            justifyContent: "center",
            width: "100%",
            // MESURÉ AU PROTOTYPE (2026-09-05) : « Laisser un avis » y est le
            // bouton PRINCIPAL — fond #233240, 52 px, rayon 14. Le montage en
            // avait fait un bouton secondaire bleu ciel de 48 px. Sur cet
            // écran, l'avis EST l'action du moment : rien d'autre n'attend.
            minHeight: 52,
            marginTop: 13,
            boxSizing: "border-box",
            borderRadius: 14,
            background: C.sejourFond,
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            fontFamily: F.sans,
            textDecoration: "none",
          }}
        >
          {libelle}
        </a>
      ) : null}
    </div>
  );
}

/**
 * LE CODE PROMO — BLOC NAVY, « −10 % » EN GRAND.
 *
 * ⚠️ GARDE-FOU DGCCRF, REPRIS À L'IDENTIQUE DE L'ÉCRAN EXISTANT. Le chiffre et
 * toute promesse de prix ne s'affichent QUE si un code promo existe réellement.
 * Sans code, le bloc reste une invitation SOBRE, sans pourcentage : annoncer
 * « −10 % » puis envoyer le voyageur au prix plein, c'est une remise non
 * délivrée. Sans adresse non plus ⇒ pas de bloc du tout (jamais de bouton mort).
 *
 * ⚠️ CE BLOC EST DU CANAL PLATEFORMES. Le serveur n'émet JAMAIS de `promo_code`
 * côté Direct — décision propriétaire ferme, « le direct est déjà le meilleur
 * tarif ». Le parcours Direct passe donc `code = null` et n'obtient que
 * l'invitation sobre. Ne pas « compléter » ce câblage sans décision de Dimitri.
 */
export function BlocPromo({
  titre,
  grandPct = null,
  sous = null,
  cta,
  href,
  code = null,
  codePrefix = null,
}) {
  if (!href || !cta) return null;
  const chiffre = code ? grandPct : null;
  return (
    <div
      data-testid="depart-promo"
      style={{
        position: "relative",
        overflow: "hidden",
        background: C.sejourFond, // même navy que le bandeau (mesuré).
        color: "#fff",
        borderRadius: 15, // MESURÉ au prototype.
        padding: "19px 17px",
        marginBottom: 11,
        boxShadow: "0 20px 44px -20px rgba(46,61,77,.5)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          right: -26,
          bottom: -28,
          opacity: 0.16,
          pointerEvents: "none",
        }}
      >
        <Lotus taille={104} couleur={C.ciel} />
      </span>
      <div style={{ position: "relative" }}>
        {/* TITRE FACULTATIF (Dimitri 2026-09-04) : en J+30, le bandeau dit
            déjà « À bientôt chez nous » — le redire ici bégaie. Sans titre,
            le contenu du bloc parle tout seul. */}
        {titre ? (
          <b
            style={{
              display: "block",
              fontFamily: F.serif,
              fontSize: 18,
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            {titre}
          </b>
        ) : null}
        {chiffre ? (
          <b
            data-testid="depart-promo-pourcent"
            style={{
              display: "block",
              fontFamily: F.serif,
              fontSize: 44,
              fontWeight: 600,
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              margin: "9px 0 2px",
              color: C.ciel,
            }}
          >
            {chiffre}
          </b>
        ) : null}
        {sous ? (
          <p
            style={{
              fontSize: 12.5,
              color: "rgba(255,255,255,.78)",
              lineHeight: 1.5,
              margin: chiffre ? "0 0 13px" : "7px 0 13px",
            }}
          >
            {sous}
          </p>
        ) : null}
        <a
          className="fbzj-cta"
          data-testid="depart-promo-lien"
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{
            ...CIBLE,
            justifyContent: "center",
            width: "100%",
            minHeight: 50,
            boxSizing: "border-box",
            borderRadius: 13,
            background: "#fff",
            color: C.bleu,
            fontSize: 15,
            fontWeight: 700,
            fontFamily: F.sans,
            textDecoration: "none",
          }}
        >
          {cta}
        </a>
        {code ? (
          <p
            data-testid="depart-promo-code"
            style={{
              fontSize: 12.5,
              color: "rgba(255,255,255,.82)",
              lineHeight: 1.5,
              margin: "11px 0 0",
              textAlign: "center",
            }}
          >
            {codePrefix ? `${codePrefix} ` : null}
            <strong
              style={{
                fontFamily: F.sans,
                letterSpacing: 1,
                fontSize: 14,
                color: "#fff",
              }}
            >
              {code}
            </strong>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * LA DÉSINSCRIPTION — DISCRÈTE, EN BAS, ET JAMAIS ABSENTE.
 *
 * 🔴 OBLIGATION LÉGALE (L.34-5 CPCE). Le message marketing d'après-séjour
 * promet ce droit ; le retirer de l'écran le rend INEXERÇABLE. Il ne disparaît
 * donc jamais — et quand le serveur n'a pas pu construire le jeton, le REPLI
 * TEXTE prend sa place (un moyen de contact clair, jamais rien, jamais un lien
 * cassé).
 *
 * Simple `<a>` : jamais un `<form>`, jamais un POST au rendu. La désinscription
 * réelle exige le clic de confirmation de la page /unsubscribe (un préchargement
 * de lien ou un antivirus de messagerie ne doit désinscrire personne).
 */
export function BlocDesinscription({ href = null, libelle, repli }) {
  return (
    <div
      style={{
        textAlign: "center",
        marginTop: 18,
        paddingTop: 15,
        borderTop: `1px solid ${C.lineSoft}`,
      }}
    >
      {href ? (
        <a
          data-testid="depart-desinscription"
          href={href}
          style={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: 44,
            color: C.inkSoft,
            fontSize: 12,
            lineHeight: 1.5,
            textDecoration: "underline",
          }}
        >
          {libelle}
        </a>
      ) : (
        <p
          data-testid="depart-desinscription-repli"
          style={{
            color: C.inkSoft,
            fontSize: 12,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {repli}
        </p>
      )}
    </div>
  );
}

/**
 * L'ORDRE DES BLOCS DU DÉPART — écrit UNE SEULE FOIS.
 *
 * Les deux parcours composent leurs données chacun de leur côté (ils ne servent
 * pas les mêmes), mais ils passent tous les deux par CE composeur. C'est la
 * pièce qui tient « je ne veux pas deux allures selon l'écran » : personne ne
 * peut réordonner un départ sans réordonner l'autre.
 *
 * Reçu, puis avis, puis promo, puis désinscription. La désinscription est
 * TOUJOURS rendue (cf. `BlocDesinscription`) ; les trois autres sont null-gatés
 * par leur appelant ET par eux-mêmes.
 */
export function BlocsDepart({ recu = null, avis = null, promo = null, desinscription }) {
  return (
    <div data-testid="depart-blocs">
      {recu ? <BlocRecuReplie {...recu} /> : null}
      {avis ? <BlocAvis {...avis} /> : null}
      {promo ? <BlocPromo {...promo} /> : null}
      <BlocDesinscription {...desinscription} />
    </div>
  );
}

// ── LE BANDEAU-CHRONO — un seul composant, deux horloges ────────────────────
//
// C'est le bloc de la carte refusée [D28], EXTRAIT tel quel pour être partagé
// avec le chrono des 48 h de l'écran « signé, à payer » (arrêt 3, 2026-09-05 :
// « le composant existe, n'en écris pas un deuxième »). Le nombre vient
// toujours d'un helper qui SAIT SE TAIRE (resteSurLeBlocage /
// resteSurLaCaducite) : ce composant ne rend RIEN sans `reste` — le silence se
// décide en amont, jamais ici.
//
// Deux tons : « corail » (l'incident — carte refusée) et « ambre » (l'attente
// pressante — les 48 h). L'ambre est MESURÉ sur la maquette (`--orBg #fdf7ee ·
// --orB #ebd9b8 · --orTx #8a5f19`, bloc `chrono()` du prototype) — contraste
// #8a5f19 sur #fdf7ee ≈ 5,9:1 ✓.
const TONS_CHRONO = {
  corail: { fond: C.corailBg, bord: C.corail, texte: C.corailTx },
  ambre: { fond: "#fdf7ee", bord: "#ebd9b8", texte: "#8a5f19" },
};

export function BandeauChrono({ reste, texte, ton = "corail", testId }) {
  if (!reste) return null;
  const t = TONS_CHRONO[ton] || TONS_CHRONO.corail;
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        background: t.fond,
        border: `1px solid ${t.bord}`,
        borderRadius: 13,
        padding: "11px 12px",
        marginBottom: 11,
      }}
    >
      <b
        style={{
          fontSize: 19,
          fontWeight: 600,
          color: t.texte,
          fontVariantNumeric: "tabular-nums",
          flexShrink: 0,
        }}
      >
        {reste.texte}
      </b>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 11.5,
          color: t.texte,
          lineHeight: 1.45,
        }}
      >
        {texte}
      </span>
    </div>
  );
}

/**
 * LE CHRONO DES 48 HEURES — écran « signé, à payer » (arrêt 3, 2026-09-05).
 *
 * L'échéance est SERVIE (`paiement_expire_le` = signature + 48 h, la même
 * horloge que le cron de caducité). Fail-soft intégral, porté par
 * `resteSurLaCaducite` : échéance absente, illisible, PASSÉE (l'écran
 * d'expiration [D32] prend le relais au refetch) ou incohérente ⇒ AUCUN
 * bandeau — jamais un chrono menteur. Statique par rendu, comme le chrono de
 * la carte refusée : l'affichage est à la minute, et l'écran se rafraîchit au
 * focus et au polling.
 */
/**
 * La durée qui reste, EN PHRASE — la mécanique du 48 h (arrêt 3) : des heures
 * en toutes lettres, et des minutes quand il reste moins d'une heure. Partagée
 * entre le message par défaut et celui de la session tombée : les deux parlent
 * du MÊME délai, ils le disent donc pareil.
 */
function dureeEnPhrase(reste) {
  const h = Math.floor(reste.totalMinutes / 60);
  return h >= 1
    ? `${h} heure${h > 1 ? "s" : ""}`
    : `${Math.max(1, Math.round(reste.totalMinutes))} minutes`;
}

export function ChronoCaducite({ echeance, maintenant = Date.now() }) {
  const reste = resteSurLaCaducite(echeance, maintenant);
  if (!reste) return null;
  // ⚠️ EN PHRASE, JAMAIS EN CADRAN (Dimitri, arrêt 3 de la revue, 2026-09-05).
  // « 28:13 » se lisait comme le minuteur de la session de paiement — DEUX
  // délais différents, deux affichages différents : le cadran chiffré reste
  // au délai de 30 min APRÈS le clic (page de paiement, carte refusée) ; le
  // délai de 48 h, lui, se dit en heures et en toutes lettres.
  const duree = dureeEnPhrase(reste);
  return (
    <div
      data-testid="sejour-chrono-caducite"
      role="status"
      style={{
        background: C.orBg,
        border: `1px solid ${C.orB}`,
        color: C.orTx,
        borderRadius: 12,
        padding: "11px 13px",
        marginBottom: 12,
        fontSize: 13,
        lineHeight: 1.5,
        fontFamily: F.sans,
      }}
    >
      Il vous reste <b>{duree}</b> pour régler votre séjour. Passé ce délai,
      vos dates repartent à la vente.
    </div>
  );
}

// ── LE REVENANT DU PAIEMENT (arrêt 3 suite, Dimitri 2026-09-05) ─────────────
//
// Le voyageur a cliqué « payer » puis est REVENU sur son espace sans payer.
// « Un voyageur ne doit jamais se demander de quel délai on lui parle » :
// TROIS cas, trois affichages, et le serveur a déjà tranché les faits
// (lecture du bloc de checkout — les mêmes champs que les deux crons).
//
//   1 · SESSION ACTIVE (`checkout_hold_expires_at` servi, encore devant nous)
//       → le cadran court de la carte refusée (resteSurLeBlocage, ≤ 35 min)
//       avec « Vos dates sont bloquées pendant que vous payez. » — PAS le 48 h.
//   2 · SESSION TOMBÉE (`checkout_session_tombee`, ou un hold servi mais
//       dépassé à l'affichage) → « Vos dates ne sont plus bloquées, mais votre
//       réservation tient encore X heures. » — X en phrase, la mécanique du
//       48 h. Sans échéance lisible, RIEN (jamais une phrase à moitié).
//   3 · JAMAIS CLIQUÉ (aucune clé servie) → l'affichage par défaut
//       (ChronoCaducite : « Il vous reste X heures pour régler votre
//       séjour… »).
//
// FAIL-CLOSED des deux côtés : le cadran ne s'affiche que sur un hold servi,
// futur et cohérent ; « plus bloquées » ne se dit que si le serveur a la
// preuve qu'une session a existé.
export const TEXTE_SESSION_ACTIVE =
  "Vos dates sont bloquées pendant que vous payez.";

export function ChronoPaiement({
  holdExpireLe = null,
  sessionTombee = false,
  echeance,
  maintenant = Date.now(),
}) {
  // Cas 1 — la session est EN COURS : le cadran chiffré court (celui de la
  // carte refusée), qui sait se taire au-delà de 35 min ou en arrière.
  const resteBlocage = resteSurLeBlocage(holdExpireLe, maintenant);
  if (resteBlocage) {
    return (
      <BandeauChrono
        testId="sejour-chrono-session"
        reste={resteBlocage}
        texte={TEXTE_SESSION_ACTIVE}
        ton="ambre"
      />
    );
  }
  // Cas 2 — une session a existé et n'est plus en cours : soit le serveur le
  // dit (`checkout_session_tombee`), soit il a servi un hold que l'affichage
  // trouve déjà dépassé (la session est tombée entre la charge et le rendu).
  const sessionExistait =
    sessionTombee === true ||
    (typeof holdExpireLe === "string" && holdExpireLe !== "");
  if (sessionExistait) {
    const reste = resteSurLaCaducite(echeance, maintenant);
    if (!reste) return null;
    return (
      <div
        data-testid="sejour-session-tombee"
        role="status"
        style={{
          background: C.orBg,
          border: `1px solid ${C.orB}`,
          color: C.orTx,
          borderRadius: 12,
          padding: "11px 13px",
          marginBottom: 12,
          fontSize: 13,
          lineHeight: 1.5,
          fontFamily: F.sans,
        }}
      >
        {/* « DEMANDE », pas « réservation » (Dimitri, arrêt 3, 2026-09-05) :
            « tant que ce n'est pas payé, ce n'est pas une réservation — le mot
            juste évite qu'un voyageur croie sa place garantie. » */}
        Vos dates ne sont plus bloquées, mais votre demande tient encore{" "}
        <b>{dureeEnPhrase(reste)}</b>. Payer maintenant les bloque à nouveau.
      </div>
    );
  }
  // Cas 3 — jamais cliqué (ou doute serveur) : l'affichage par défaut.
  return <ChronoCaducite echeance={echeance} maintenant={maintenant} />;
}

// ── L'ÉCRAN D'EXCEPTION ─────────────────────────────────────────────────────

/**
 * Les douze chemins, un seul écran. [D27]
 *
 * Il porte toujours : ce qui s'est passé (titre), ce que ça change (message),
 * la suite, et un geste — ou, quand le seul recours est de nous écrire, le
 * renvoi vers le pied de page qui porte le contact. Jamais un cul-de-sac.
 */
export function EcranException({
  cle,
  datesDisponibles,
  urlRefaire,
  blocageExpireLe,
  maintenant,
  onReessayer,
  messageServeur,
  // [arrêt 25, 2026-09-06] « contactez-nous » sans moyen de le faire = écran
  // muet : le bouton standard du parcours vit AUSSI ici (le pied de page se
  // tait quand l'adresse est servie — c'est donc ce bouton qui porte la
  // garantie). Absent sans adresse : le pied de repli reprend alors la main.
  emailContact = null,
}) {
  const base =
    cle === "sejour_annule"
      ? enrichirAnnule({ datesDisponibles, urlRefaire })
      : cle === "delai_ecoule"
        ? // [D32] Délai de 48 h écoulé : MÊME montage que le séjour annulé
          // (disponibilité réelle servie par le serveur, bouton retiré quand
          // les dates sont formellement prises).
          enrichirDelaiEcoule({ datesDisponibles, urlRefaire })
        : cle === "signature_expiree"
          ? // [arrêt 28, 2026-09-06] l'expiré générique porte lui aussi le
            // rattrapage — reprendre depuis le site, mêmes garde-fous.
            enrichirSignatureExpiree({ datesDisponibles, urlRefaire })
          : lireChemin(cle);
  if (!base) return null;
  const mauvais = base.ton === "mauvais";
  const reste =
    cle === "carte_refusee"
      ? resteSurLeBlocage(blocageExpireLe, maintenant)
      : null;

  return (
    <div data-testid="sejour-exception" data-chemin={cle}>
      <Bandeau
        titre={base.titre}
        dates={base.message}
        testId="sejour-exception-bandeau"
      />

      {/* [D28] Le chrono — SEULEMENT quand l'instant d'expiration est servi et
          cohérent. Sinon la phrase statique, qui ne ment sur rien. */}
      {cle === "carte_refusee" ? (
        reste ? (
          <BandeauChrono
            testId="sejour-chrono"
            reste={reste}
            texte={TEXTE_BLOCAGE}
            ton="corail"
          />
        ) : (
          <p
            data-testid="sejour-chrono-repli"
            style={{
              fontSize: 12,
              color: C.inkSoft,
              lineHeight: 1.55,
              margin: "0 0 11px",
            }}
          >
            {TEXTE_BLOCAGE_SANS_CHRONO}
          </p>
        )
      ) : null}

      {/* La rassurance de la carte refusée : l'argent d'abord. */}
      {base.rassurance ? (
        <div
          data-testid="sejour-exception-rassurance"
          style={{
            background: mauvais ? "#fef2f2" : C.cielBg,
            border: `1px solid ${mauvais ? C.red : C.ciel}`,
            color: mauvais ? C.redTx : C.bleu,
            borderRadius: 14,
            padding: "12px 13px",
            marginBottom: 12,
            fontSize: 12.5,
            lineHeight: 1.6,
          }}
        >
          {base.rassurance}
        </div>
      ) : null}

      {/* La disponibilité des dates d'un séjour annulé — le seul rattrapage
          commercial du parcours ; le serveur la calcule POUR cet écran. */}
      {base.dispo ? (
        <p
          data-testid="sejour-exception-dispo"
          data-dispo={
            datesDisponibles === true
              ? "oui"
              : datesDisponibles === false
                ? "non"
                : "inconnu"
          }
          style={{
            fontSize: 13,
            color: C.ink,
            lineHeight: 1.6,
            margin: "0 0 14px",
          }}
        >
          {base.dispo}
        </p>
      ) : null}

      {/* Un message précis renvoyé par le serveur prime sur le texte générique
          (le 503 du contrat en porte un). */}
      {messageServeur && messageServeur !== base.message ? (
        <p
          style={{
            fontSize: 13,
            color: C.inkSoft,
            lineHeight: 1.6,
            margin: "0 0 14px",
          }}
        >
          {messageServeur}
        </p>
      ) : null}

      {base.geste.type === "refaire" ? (
        <BoutonPlein testId="sejour-exception-refaire" href={base.geste.href}>
          {base.geste.libelle}
        </BoutonPlein>
      ) : null}
      {base.geste.type === "reessayer" && onReessayer ? (
        <BoutonPlein testId="sejour-exception-reessayer" onClick={onReessayer}>
          {base.geste.libelle}
        </BoutonPlein>
      ) : null}
      <BoutonJoindre email={emailContact} />
    </div>
  );
}

// ── utilitaires d'affichage ────────────────────────────────────────────────

/** « 2026-09-18 » → « 18/09 ». Rend null si la date est illisible. */
export function formatJourMois(iso) {
  if (typeof iso !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}` : null;
}

export { ETAPES, textesEtape };
